import React from 'react';

import { MediaSlot } from './MediaSlot';
import { MissingCreativeBand } from './MissingCreativeBand';
import { PLATFORM_SKINS } from './platform-tokens';
import { resolveAspectRatio } from './types';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * Meta's DESKTOP RIGHT-COLUMN ad unit — a small thumbnail beside a headline,
 * with no primary text and no engagement row. Genuinely a different unit
 * from the in-feed card `MetaAdPreview` renders: Meta's right-column
 * placement has no room for a caption, a like/comment/share row, or a
 * multi-line link block, so routing it through the feed shell (as the prior
 * grid did) rendered chrome the placement does not have — a buyer could
 * approve copy that would never appear there. See
 * `meta-placement-catalog.ts`'s `right-column` frame and
 * `MetaPlacementPreviewGrid`'s P0 fix log.
 *
 * No Figma frame exists for this unit in `Dl0gE7lH27N7x7OJb0DFBw` — unlike
 * every other replica in this directory, this one is NOT a pixel-measured
 * port. It is built from Meta's own publicly documented right-column shape
 * (small thumbnail, one-line headline, sponsor label, no body copy) using
 * this directory's existing skin tokens, and should be treated as a
 * best-effort approximation, not a verified-parity replica, until a real
 * reference is pulled.
 */
export interface MetaRightColumnAdPreviewProps {
  brandName: string;
  headline: string;
  destinationDomain?: string | null;
  imageUrl?: string | null;
  videoUrl?: string | null;
  aspectRatio?: number | string | null;
  isLoading?: boolean;
  emptyHint?: string;
  /**
   * Forces which Facebook skin variant renders, independent of Mora's
   * active theme — same contract as `MetaAdPreview.theme`.
   */
  theme?: 'light' | 'dark';
  /** Rendered inside a frame that already sizes it — same contract as the other replicas in this directory. */
  embedded?: boolean;
  className?: string;
}

/**
 * Fallback ratio when a caller omits `aspectRatio` — the one real call site
 * (`MetaPlacementPreviewGrid`'s `FrameContent`) always passes
 * `frame.nativeAspectRatio`, which for `right-column` is `LINK_RATIO`
 * (1.91:1, `meta-placement-catalog.ts`) — the classic wide link-ad crop,
 * not a square one. Matched to that value 2026-09-03: the previous `1`
 * here contradicted the only frame that reaches this component and the
 * "Cropped to fit" verdict it drives, which both use 1.91:1.
 */
const RIGHT_COLUMN_MEDIA_RATIO = 1.91;
/** The unit's own natural width — narrow, unlike the full-width feed shells. */
const RIGHT_COLUMN_MAX_WIDTH = 254;

export const MetaRightColumnAdPreview: React.FC<MetaRightColumnAdPreviewProps> = ({
  brandName,
  headline,
  destinationDomain,
  imageUrl,
  videoUrl,
  aspectRatio,
  isLoading = false,
  emptyHint = 'No creative yet — copy only',
  theme,
  embedded = false,
  className,
}) => {
  const isDarkMode = useResolvedDarkMode();
  const resolvedTheme = theme ?? (isDarkMode ? 'dark' : 'light');
  const skin = PLATFORM_SKINS.facebook[resolvedTheme];
  const domain = destinationDomain?.trim();
  const resolvedAspect = resolveAspectRatio(aspectRatio, RIGHT_COLUMN_MEDIA_RATIO);
  const hasMedia = Boolean(imageUrl) || Boolean(videoUrl) || isLoading;

  return (
    <div
      className={cn(embedded ? 'w-full' : 'mx-auto w-full', className)}
      style={embedded ? undefined : { maxWidth: `${RIGHT_COLUMN_MAX_WIDTH}px` }}
    >
      <article
        className="flex w-full items-start gap-2 overflow-hidden rounded-md"
        data-platform="meta-right-column-ad"
        style={{ backgroundColor: skin.cardBackground }}
      >
        <div className="w-[74px] shrink-0 overflow-hidden rounded-sm">
          {hasMedia ? (
            <MediaSlot
              ratio={resolvedAspect}
              bg="muted"
              imageUrl={imageUrl}
              videoUrl={videoUrl}
              isLoading={isLoading}
              emptyHint={emptyHint}
              alt={headline || `${brandName} ad creative`}
            />
          ) : (
            <MissingCreativeBand hint={emptyHint} aspectRatio={RIGHT_COLUMN_MEDIA_RATIO} />
          )}
        </div>
        <div className="min-w-0 flex-1 py-0.5">
          <p
            className="truncate text-micro font-medium tracking-wide"
            style={{ color: skin.secondaryText }}
          >
            Sponsored
          </p>
          <p
            className="mt-0.5 line-clamp-2 text-caption font-semibold leading-snug"
            style={{ color: skin.primaryText }}
          >
            {headline || 'No headline'}
          </p>
          <p
            className={cn('mt-0.5 truncate', domain ? 'text-micro' : 'text-[13px] leading-5')}
            style={{ color: skin.secondaryText, opacity: domain ? 1 : 0.85 }}
          >
            {domain ? domain : 'No destination URL yet'}
          </p>
        </div>
      </article>
    </div>
  );
};
